import React, { useEffect, useState } from 'react';
import { Card, Row, Col } from 'react-bootstrap';
import { ResponsiveLine } from '@nivo/line';
import CsvDownloadButton from 'react-json-to-csv';
import { getCycleDailyStats } from '../../../services/v2/plantingCycleService';
import '../../../styles/graphs.css';

const PhaseMarkerLayer = ({ xScale, innerHeight, phaseReferences, color }) => {
    if (!phaseReferences?.length) return null;
    const [xMin, xMax] = xScale.domain();
    return (
        <>
            {phaseReferences.map((ref, i) => {
                const date = ref.actualCompletionDate || ref.estimatedCompletionDate;
                if (!date) return null;
                const d = new Date(date + 'T00:00:00');
                if (d < xMin || d > xMax) return null;
                const x = xScale(d);
                const isDashed = !ref.actualCompletionDate;
                return (
                    <g key={i}>
                        <line
                            x1={x} x2={x}
                            y1={0} y2={innerHeight}
                            stroke={color}
                            strokeWidth={1.5}
                            strokeDasharray={isDashed ? '4 3' : undefined}
                            opacity={0.6}
                        />
                        <text
                            x={x + 4}
                            y={8}
                            fill={color}
                            fontSize={8}
                            fontWeight="bold"
                            transform={`rotate(-60 ${x + 4} 8)`}
                        >
                            {ref.phaseName}
                        </text>
                    </g>
                );
            })}
        </>
    );
};

const useIsMobile = () => {
    const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);

    useEffect(() => {
        const mql = window.matchMedia('(max-width: 767px)');
        const handler = (e) => setIsMobile(e.matches);
        mql.addEventListener('change', handler);
        return () => mql.removeEventListener('change', handler);
    }, []);

    return isMobile;
};

const GddDliAccumulatedChart = ({ plantingCycle }) => {
    const [statsData, setStatsData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const isMobile = useIsMobile();

    useEffect(() => {
        if (!plantingCycle?.id) return;
        setLoading(true);
        getCycleDailyStats(plantingCycle.id)
            .then(data => {
                setStatsData(data);
                setError(null);
            })
            .catch(() => setError('No se pudieron cargar los datos'))
            .finally(() => setLoading(false));
    }, [plantingCycle?.id]);

    const stats = statsData?.stats || [];
    const phaseReferences = statsData?.phaseReferences || [];

    const gddData = [{
        id: 'GDD Acumulado',
        data: stats.map(s => ({ x: s.date, y: s.accumulatedGddToDate ?? 0 }))
    }];

    const dliData = [{
        id: 'DLI Diario',
        data: stats.map(s => ({ x: s.date, y: s.dailyDli ?? 0 }))
    }];

    const chillData = [{
        id: 'Frío Acumulado',
        data: stats.map(s => ({ x: s.date, y: s.accumulatedChillPortionsToDate ?? 0 }))
    }];

    const hasChillPhase = (plantingCycle?.phaseLogs || []).some(p => p.completionMetric === 'CHILL');

    const prepareCsvData = (valueKey, valueLabel) => stats.map(s => ({
        Fecha: s.date,
        [valueLabel]: s[valueKey] ?? 0
    }));

    const commonChartProps = {
        margin: { top: 30, right: 30, bottom: 60, left: 60 },
        xScale: { type: 'time', format: '%Y-%m-%d', precision: 'day', useUTC: false },
        xFormat: 'time:%Y-%m-%d',
        yScale: { type: 'linear', min: 0, max: 'auto' },
        yFormat: ',.2f',
        axisTop: null,
        axisRight: null,
        axisBottom: {
            format: '%d %b',
            tickValues: isMobile ? 'every 15 days' : 'every 5 days',
            tickSize: 5,
            tickPadding: 10,
            tickRotation: 45,
            legend: 'Día',
            legendOffset: 45,
            legendPosition: 'middle'
        },
        enableGridX: false,
        lineWidth: 3,
        pointSize: isMobile ? 0 : 4,
        pointColor: { from: 'color' },
        pointBorderWidth: 2,
        pointBorderColor: { from: 'serieColor' },
        enableArea: true,
        areaOpacity: 0.15,
        curve: 'monotoneX',
        legends: [],
        enableSlices: 'x',
        sliceTooltip: ({ slice }) => (
            <div style={{
                background: 'white',
                padding: '9px 12px',
                border: '1px solid #ccc',
                borderRadius: '6px',
                fontSize: '12px'
            }}>
                <div style={{ marginBottom: '6px' }}>
                    <strong>Fecha: {slice.points[0].data.xFormatted}</strong>
                </div>
                {slice.points.map((point) => (
                    <div key={point.id} style={{ color: point.serieColor }}>
                        <strong>{point.serieId}:</strong> {point.data.yFormatted}
                    </div>
                ))}
            </div>
        ),
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-40 text-gray-400 text-xs">
                Cargando datos...
            </div>
        );
    }

    if (error || !stats.length) {
        return (
            <div className="flex items-center justify-center h-40 text-gray-400 text-xs">
                {error || 'Sin datos disponibles aún'}
            </div>
        );
    }

    return (
        <Row className="g-4">
            {/* GDD Acumulado */}
            <Col xs={12}>
                <Card className="border-0 bg-gray-50/50 rounded-xl overflow-hidden">
                    <Card.Header className="bg-transparent border-0 pt-4 px-4 pb-0 d-flex justify-content-between align-items-center">
                        <h6 className="mb-0 text-gray-600 font-bold uppercase text-[10px] tracking-widest">
                            GDD Acumulado
                        </h6>
                        <CsvDownloadButton
                            data={prepareCsvData('accumulatedGddToDate', 'GDD Acumulado')}
                            filename="gdd_acumulado"
                            headers={['Fecha', 'GDD Acumulado']}
                            title="Descargar CSV"
                        >
                            <div className="ButtonCSV" />
                        </CsvDownloadButton>
                    </Card.Header>
                    <div className="p-2" style={{ height: '320px' }}>
                        <ResponsiveLine
                            data={gddData}
                            {...commonChartProps}
                            colors={['#f97316']}
                            axisLeft={{
                                tickSize: 5, tickPadding: 5, tickRotation: 0,
                                legend: 'GDD', legendOffset: -50, legendPosition: 'middle'
                            }}
                            layers={[
                                'grid', 'axes', 'areas', 'lines', 'points', 'slices', 'mesh', 'legends',
                                (props) => <PhaseMarkerLayer {...props} phaseReferences={phaseReferences} color="#6366f1" />,
                            ]}
                        />
                    </div>
                </Card>
            </Col>

            {/* DLI Acumulado */}
            <Col xs={12}>
                <Card className="border-0 bg-gray-50/50 rounded-xl overflow-hidden">
                    <Card.Header className="bg-transparent border-0 pt-4 px-4 pb-0 d-flex justify-content-between align-items-center">
                        <h6 className="mb-0 text-gray-600 font-bold uppercase text-[10px] tracking-widest">
                            DLI Diario
                        </h6>
                        <CsvDownloadButton
                            data={prepareCsvData('dailyDli', 'DLI Diario')}
                            filename="dli_diario"
                            headers={['Fecha', 'DLI Diario']}
                            title="Descargar CSV"
                        >
                            <div className="ButtonCSV" />
                        </CsvDownloadButton>
                    </Card.Header>
                    <div className="p-2" style={{ height: '320px' }}>
                        <ResponsiveLine
                            data={dliData}
                            {...commonChartProps}
                            colors={['#eab308']}
                            axisLeft={{
                                tickSize: 5, tickPadding: 5, tickRotation: 0,
                                legend: 'DLI (mol/m²/día)', legendOffset: -50, legendPosition: 'middle'
                            }}
                            layers={[
                                'grid', 'axes', 'areas', 'lines', 'points', 'slices', 'mesh', 'legends',
                                (props) => <PhaseMarkerLayer {...props} phaseReferences={phaseReferences} color="#6366f1" />,
                            ]}
                        />
                    </div>
                </Card>
            </Col>

            {/* Frío Acumulado (Chill Portions) — solo para ciclos con fase de dormancia */}
            {hasChillPhase && (
                <Col xs={12}>
                    <Card className="border-0 bg-gray-50/50 rounded-xl overflow-hidden">
                        <Card.Header className="bg-transparent border-0 pt-4 px-4 pb-0 d-flex justify-content-between align-items-center">
                            <h6 className="mb-0 text-gray-600 font-bold uppercase text-[10px] tracking-widest">
                                Frío Acumulado (Chill Portions)
                            </h6>
                            <CsvDownloadButton
                                data={prepareCsvData('accumulatedChillPortionsToDate', 'Frío Acumulado')}
                                filename="frio_acumulado"
                                headers={['Fecha', 'Frío Acumulado']}
                                title="Descargar CSV"
                            >
                                <div className="ButtonCSV" />
                            </CsvDownloadButton>
                        </Card.Header>
                        <div className="p-2" style={{ height: '320px' }}>
                            <ResponsiveLine
                                data={chillData}
                                {...commonChartProps}
                                colors={['#3b82f6']}
                                axisLeft={{
                                    tickSize: 5, tickPadding: 5, tickRotation: 0,
                                    legend: 'CHP', legendOffset: -50, legendPosition: 'middle'
                                }}
                                layers={[
                                    'grid', 'axes', 'areas', 'lines', 'points', 'slices', 'mesh', 'legends',
                                    (props) => <PhaseMarkerLayer {...props} phaseReferences={phaseReferences} color="#6366f1" />,
                                ]}
                            />
                        </div>
                    </Card>
                </Col>
            )}
        </Row>
    );
};

export default GddDliAccumulatedChart;